import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronLeft, IconX } from '@tabler/icons-react';
import { Button, IconButton, Loader, Text } from '@wapharma/design-system';
import { useTour } from '../contexts/TourContext';
import {
  isAwaitNone,
  isClickSelectorAwait,
  isForceClickStep,
  isSelectorVisibleAwait,
  resolveStepAwait,
  type TourPlacement,
} from '../tours/types';
import { TOURS_ENABLED } from '../tours/flags';
import { isElementVisible, queryVisible } from '../tours/waitForElement';
import { StepTravel } from './StepTravel';
import styles from './TourOverlay.module.css';

const HOLE_PAD = 6;
const GAP = 12;
const DOCK_MARGIN = 24;
const PANEL_W = 280;
const PANEL_H = 148;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

type PanelLayout = {
  top: number;
  left: number;
  docked: boolean;
  arrow: TourPlacement | null;
  arrowX: number;
  arrowY: number;
};

type HoleRect = { top: number; left: number; width: number; height: number };

function unionHole(a: HoleRect, b: DOMRect | HoleRect): HoleRect {
  const top = Math.min(a.top, b.top);
  const left = Math.min(a.left, b.left);
  const right = Math.max(a.left + a.width, b.left + b.width);
  const bottom = Math.max(a.top + a.height, b.top + b.height);
  return { top, left, width: right - left, height: bottom - top };
}

function padHole(r: HoleRect, pad: number): HoleRect {
  return {
    top: r.top - pad,
    left: r.left - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };
}

function companionDockClearance(): number {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--wp-companion-dock-pad')
    .trim();
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) && px > 0 ? px : 0;
}

function panelLayout(rect: DOMRect | null, placement: TourPlacement): PanelLayout {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const panelW = Math.min(PANEL_W, vw - 32);
  const bottomClear = DOCK_MARGIN + companionDockClearance();
  const maxTop = Math.max(16, vh - PANEL_H - bottomClear);

  if (!rect) {
    return {
      top: maxTop,
      left: clamp(vw - panelW - DOCK_MARGIN, 16, vw - panelW - 16),
      docked: true,
      arrow: null,
      arrowX: 24,
      arrowY: 24,
    };
  }

  const wideBand = rect.width > vw * 0.55;
  const pick = placement === 'auto' ? 'bottom' : placement;

  if (pick === 'bottom') {
    const top = clamp(rect.bottom + GAP, 16, maxTop);
    const left = wideBand
      ? clamp(rect.right - panelW, 16, vw - panelW - 16)
      : clamp(rect.left + rect.width / 2 - panelW / 2, 16, vw - panelW - 16);
    const arrowX = clamp(rect.left + Math.min(rect.width * 0.75, rect.width - 24) - left, 16, panelW - 32);
    return { top, left, docked: false, arrow: 'bottom', arrowX, arrowY: 0 };
  }

  if (pick === 'top') {
    const top = clamp(rect.top - PANEL_H - GAP, 16, maxTop);
    const left = clamp(rect.left + rect.width / 2 - panelW / 2, 16, vw - panelW - 16);
    const arrowX = clamp(rect.left + rect.width / 2 - left, 16, panelW - 32);
    return { top, left, docked: false, arrow: 'top', arrowX, arrowY: 0 };
  }

  if (pick === 'left') {
    const top = clamp(rect.top + rect.height / 2 - PANEL_H / 2, 16, maxTop);
    const left = clamp(rect.left - panelW - GAP, 16, vw - panelW - 16);
    const arrowY = clamp(rect.top + rect.height / 2 - top, 20, PANEL_H - 32);
    return { top, left, docked: false, arrow: 'left', arrowX: 0, arrowY };
  }

  const top = clamp(rect.top + rect.height / 2 - PANEL_H / 2, 16, maxTop);
  const left = clamp(rect.right + GAP, 16, vw - panelW - 16);
  const arrowY = clamp(rect.top + rect.height / 2 - top, 20, PANEL_H - 32);
  return { top, left, docked: false, arrow: 'right', arrowX: 0, arrowY };
}

/** Grow hole to cover pulsed target + revealed flyout / await targets. */
function measureHole(
  targetRect: DOMRect | null,
  awaitSpec: ReturnType<typeof resolveStepAwait>,
): HoleRect | null {
  if (!targetRect) return null;
  let hole: HoleRect = {
    top: targetRect.top,
    left: targetRect.left,
    width: targetRect.width,
    height: targetRect.height,
  };

  const extraSelector = isClickSelectorAwait(awaitSpec)
    ? awaitSpec.clickSelector
    : isSelectorVisibleAwait(awaitSpec)
      ? awaitSpec.selectorVisible
      : null;

  if (extraSelector) {
    const el = queryVisible(extraSelector);
    if (el) {
      hole = unionHole(hole, el.getBoundingClientRect());
      const menu = el.closest('[role="menu"]');
      if (menu instanceof HTMLElement && isElementVisible(menu)) {
        hole = unionHole(hole, menu.getBoundingClientRect());
      }
    }
  }

  /* Open cluster flyout (portaled) — include even before item query hits. */
  const marked = document.querySelector('[data-wp-tour-active]');
  if (marked instanceof HTMLElement && marked.getAttribute('aria-expanded') === 'true') {
    for (const menu of document.querySelectorAll('[role="menu"]')) {
      if (!(menu instanceof HTMLElement) || !isElementVisible(menu)) continue;
      hole = unionHole(hole, menu.getBoundingClientRect());
    }
  }

  /* In-page create menu (not portaled) — must sit inside hole or pads block it. */
  const createWrap = document.querySelector('[data-tour="catalog-create"]');
  if (createWrap instanceof HTMLElement) {
    const createMenu = createWrap.querySelector('[role="menu"]');
    if (createMenu instanceof HTMLElement && isElementVisible(createMenu)) {
      hole = unionHole(hole, createWrap.getBoundingClientRect());
      hole = unionHole(hole, createMenu.getBoundingClientRect());
    }
  }

  return padHole(hole, HOLE_PAD);
}

export function TourOverlay() {
  const {
    active,
    currentStep,
    targetRect,
    waiting,
    outcomePending,
    awaitMet,
    missing,
    next,
    prev,
    stopTour,
    finishTour,
  } = useTour();
  const panelRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PanelLayout>(() => panelLayout(null, 'auto'));
  const [hole, setHole] = useState<HoleRect | null>(null);

  useLayoutEffect(() => {
    if (!TOURS_ENABLED || !active || !currentStep) return;
    const awaitSpec = resolveStepAwait(currentStep);
    const placement = currentStep.placement ?? 'auto';

    const refresh = () => {
      const marked = document.querySelector('[data-wp-tour-active]');
      const live =
        marked instanceof HTMLElement ? marked.getBoundingClientRect() : targetRect;
      const nextHole = waiting ? null : measureHole(live, awaitSpec);
      setHole(nextHole);
      /* Anchor tip to full spotlight (trigger + flyout), not trigger alone. */
      const anchor = nextHole
        ? new DOMRect(nextHole.left, nextHole.top, nextHole.width, nextHole.height)
        : waiting
          ? null
          : targetRect;
      setLayout(panelLayout(anchor, placement));
    };
    refresh();

    if (waiting || !targetRect) return;
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    const timer = window.setInterval(refresh, 200);
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
      window.clearInterval(timer);
    };
  }, [active, currentStep, targetRect, waiting]);

  if (!TOURS_ENABLED || !active || !currentStep) return null;

  const stepNum = active.index + 1;
  const stepTotal = active.steps.length;
  const isFirst = active.index <= 0;
  const isLast = active.index >= active.steps.length - 1;
  const awaitSpec = resolveStepAwait(currentStep);
  const needsAwait = !isAwaitNone(awaitSpec);
  const forceClick = isForceClickStep(currentStep);
  /* Free-Suivant coach: do not let hole clicks hit row chrome (Accès → detail). */
  const holeClicksPass = forceClick;
  const primaryDisabled = waiting || (needsAwait && !awaitMet);
  const showAwaitHint = needsAwait && !awaitMet && !waiting;
  const instruction = currentStep.body.trim() || currentStep.title;
  const showPrimary = isLast || (!forceClick && !needsAwait) || (needsAwait && awaitMet && !forceClick);
  const primaryLabel = isLast ? 'Terminer' : 'Suivant';

  const panelClass = layout.docked
    ? `${styles.panel} ${styles.panelDocked}`
    : `${styles.panel} ${styles.panelAnchored}`;

  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;

  return createPortal(
    <div className={styles.root} role="presentation" data-wp-tour-root="">
      {hole ? (
        <>
          {/* Blocking pads — clicks outside hole die here; hole + flyouts above stay live. */}
          <div
            className={styles.pad}
            style={{ top: 0, left: 0, width: vw, height: Math.max(0, hole.top) }}
            aria-hidden
          />
          <div
            className={styles.pad}
            style={{
              top: hole.top,
              left: 0,
              width: Math.max(0, hole.left),
              height: hole.height,
            }}
            aria-hidden
          />
          <div
            className={styles.pad}
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              width: Math.max(0, vw - (hole.left + hole.width)),
              height: hole.height,
            }}
            aria-hidden
          />
          <div
            className={styles.pad}
            style={{
              top: hole.top + hole.height,
              left: 0,
              width: vw,
              height: Math.max(0, vh - (hole.top + hole.height)),
            }}
            aria-hidden
          />
          <div
            className={styles.hole}
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
            }}
            aria-hidden
          />
          {!holeClicksPass ? (
            <div
              className={styles.holeBlock}
              style={{
                top: hole.top,
                left: hole.left,
                width: hole.width,
                height: hole.height,
              }}
              aria-hidden
            />
          ) : null}
        </>
      ) : (
        <div className={styles.padFull} aria-hidden />
      )}

      <div
        ref={panelRef}
        className={panelClass}
        data-arrow={layout.arrow ?? undefined}
        style={{
          top: layout.top,
          left: layout.left,
          ['--tour-arrow-x' as string]: `${layout.arrowX}px`,
          ['--tour-arrow-y' as string]: `${layout.arrowY}px`,
        }}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
      >
        <span id="tour-title" className={styles.srOnly}>
          {currentStep.title}
        </span>

        <div className={styles.panelTop}>
          <ol className={styles.dots} aria-label={`Étape ${stepNum} sur ${stepTotal}`}>
            {active.steps.map((step, i) => (
              <li
                key={step.id}
                className={i === active.index ? styles.dotActive : styles.dot}
                aria-current={i === active.index ? 'step' : undefined}
              />
            ))}
          </ol>
          <IconButton
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Quitter le parcours"
            onClick={stopTour}
          >
            <IconX size={14} stroke={1.7} />
          </IconButton>
        </div>

        {waiting || outcomePending ? (
          <div className={styles.panelWaiting}>
            <Loader size="sm" />
            <Text size="xs">
              {outcomePending ? 'En attente de l’action…' : 'Chargement…'}
            </Text>
          </div>
        ) : (
          <StepTravel index={active.index}>
            <div className={styles.panelCopy}>
              <p id="tour-body" className={styles.panelBody}>
                {instruction}
              </p>
              {showAwaitHint ? (
                <p className={styles.panelAwaitHint}>
                  {forceClick
                    ? 'Cliquez le contrôle mis en avant.'
                    : 'Faites l’action, puis le guide continue.'}
                </p>
              ) : null}
            </div>
          </StepTravel>
        )}

        {missing ? (
          <Text size="xs" className={styles.panelMissing}>
            {missing}
          </Text>
        ) : null}

        <div className={styles.panelActions}>
          {!isFirst ? (
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Précédent"
              disabled={waiting}
              onClick={prev}
            >
              <IconChevronLeft size={18} stroke={1.7} />
            </IconButton>
          ) : (
            <span className={styles.panelNavSpacer} aria-hidden />
          )}
          {showPrimary || isLast ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={primaryDisabled}
              onClick={() => {
                if (isLast) finishTour();
                else next();
              }}
            >
              {primaryLabel}
            </Button>
          ) : (
            <span className={styles.panelNavSpacer} aria-hidden />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
